/**
 * Add/edit form for a record.
 *
 * Core fields stay visible. Everything else lives behind an "Advanced"
 * disclosure so the default path feels as simple as the old GetReminded flow:
 * kind → name → date → (cost) → save.
 *
 * Two things still drive the design:
 *
 * 1. The form adapts to `kind`. A passport has no billing cycle and no notice
 *    period, so showing those fields would be irrelevant inputs to skip.
 * 2. Cost is edited in major units ("10.99") but stored in minor units (1099),
 *    converted once on submit. The raw string is kept in state while typing so
 *    a half-typed "10." is not destroyed by a round-trip through a number.
 */

import { ChevronDown } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'

import { toDateOnlyString, addDays, formatDate, parseDateOnly, today } from '@/domain/dates'
import { defaultsForKind, kindHasCost, kindHasNotice } from '@/domain/items'
import { minorToMajorInput, parseMajorToMinor } from '@/domain/money'
import {
  BILLING_CYCLE_LABELS,
  BILLING_CYCLES,
  CURRENCIES,
  DEFAULT_OFFSETS,
  KIND_LABELS,
  KINDS,
  RENEWAL_PERIOD_LABELS,
  RENEWAL_PERIODS,
} from '@/domain/types'
import type {
  BillingCycle,
  Currency,
  ItemInput,
  ItemRecord,
  Kind,
  RenewalPeriod,
} from '@/domain/types'
import { Alert, Button, Card, Checkbox, Field, Input, Select, Textarea } from './ui'
import { cn } from '@/lib/cn'

export interface ItemFormValues {
  input: ItemInput
}

interface Props {
  existing?: ItemRecord | undefined
  submitLabel: string
  busy?: boolean
  error?: string | undefined
  onSubmit: (values: ItemFormValues) => void
  onCancel: () => void
  onDelete?: (() => void) | undefined
  /** Archive keeps cost history; preferred over delete for finished records. */
  onArchive?: (() => void) | undefined
  onReactivate?: (() => void) | undefined
  statusBusy?: boolean
}

/** True when any advanced field differs from a sensible blank/default. */
function hasAdvancedValues(existing: ItemRecord | undefined): boolean {
  if (!existing) return false
  if (existing.reminder_offsets && existing.reminder_offsets.length > 0) return true
  if (existing.note?.trim()) return true
  return false
}

const KIND_PLACEHOLDERS: Record<Kind, { name: string; provider: string }> = {
  document: { name: 'Passport', provider: 'HM Passport Office' },
  subscription: { name: 'Netflix', provider: 'Netflix' },
  contract: { name: 'Broadband', provider: 'Zen Internet' },
  other: { name: 'Amex bonus miles', provider: 'American Express' },
}

export function ItemForm({
  existing,
  submitLabel,
  busy = false,
  error,
  onSubmit,
  onCancel,
  onDelete,
  onArchive,
  onReactivate,
  statusBusy = false,
}: Props) {
  const initialKind: Kind = existing?.kind ?? 'subscription'
  const initialDefaults = defaultsForKind(initialKind)

  const [kind, setKind] = useState<Kind>(initialKind)
  const [name, setName] = useState(existing?.name ?? '')
  const [provider, setProvider] = useState(existing?.provider ?? '')
  const [expiryDate, setExpiryDate] = useState(
    existing ? existing.expiry_date.slice(0, 10) : toDateOnlyString(addDays(today(), 30)),
  )
  const [noticeDays, setNoticeDays] = useState(
    String(existing?.notice_period_days ?? initialDefaults.notice_period_days),
  )
  const [autoRenews, setAutoRenews] = useState(existing?.auto_renews ?? initialDefaults.auto_renews)
  const [renewalPeriod, setRenewalPeriod] = useState<RenewalPeriod>(
    existing?.renewal_period ?? initialDefaults.renewal_period,
  )
  const [cost, setCost] = useState(minorToMajorInput(existing?.cost_minor ?? 0))
  const [currency, setCurrency] = useState<Currency>(existing?.currency ?? 'GBP')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(
    existing?.billing_cycle ?? initialDefaults.billing_cycle,
  )
  const [offsets, setOffsets] = useState(
    (existing?.reminder_offsets ?? []).join(', '),
  )
  const [note, setNote] = useState(existing?.note ?? '')
  const [validation, setValidation] = useState('')
  // Open Advanced automatically on edit when something non-default is already set.
  const [advancedOpen, setAdvancedOpen] = useState(() => hasAdvancedValues(existing))

  const showCost = kindHasCost(kind)
  const showNotice = kindHasNotice(kind)
  const isActive = !existing || existing.status === 'active'
  const placeholders = KIND_PLACEHOLDERS[kind]

  /** Preview of the date reminders actually key off. */
  const actionByPreview = useMemo(() => {
    if (!expiryDate) return null
    const days = Number(noticeDays)
    if (!Number.isFinite(days) || days < 0) return null
    return addDays(parseDateOnly(expiryDate), -days)
  }, [expiryDate, noticeDays])

  /** Switching kind re-applies that kind's sensible defaults, but keeps the text. */
  function changeKind(next: Kind) {
    setKind(next)
    const defaults = defaultsForKind(next)
    setNoticeDays(String(defaults.notice_period_days))
    setAutoRenews(defaults.auto_renews)
    setRenewalPeriod(defaults.renewal_period)
    setBillingCycle(defaults.billing_cycle)
    if (!kindHasCost(next)) setCost('')
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setValidation('')

    if (!name.trim()) {
      setValidation('Give the record a name.')
      return
    }
    if (!expiryDate) {
      setValidation('Set the expiry or renewal date.')
      return
    }

    const costMinor = showCost ? parseMajorToMinor(cost) : 0
    if (costMinor === null) {
      setValidation('Cost must be a number, like 10.99.')
      return
    }

    const noticeParsed = showNotice ? Number(noticeDays || 0) : 0
    if (!Number.isInteger(noticeParsed) || noticeParsed < 0) {
      setValidation('Days before expiry must be a whole number, or 0.')
      return
    }

    const parsedOffsets = parseOffsets(offsets)
    if (parsedOffsets === null) {
      setValidation('Reminder days must be whole numbers, like: 90, 30, 7, 1')
      return
    }

    // A cost with no cycle would silently count as £0/year in the totals.
    const cycle: BillingCycle = showCost ? billingCycle : 'one_off'
    if (costMinor > 0 && cycle === 'one_off' && renewalPeriod === 'none' && autoRenews) {
      setValidation('An auto-renewing record needs a renewal period.')
      return
    }

    onSubmit({
      input: {
        kind,
        name: name.trim(),
        provider: provider.trim(),
        expiry_date: expiryDate,
        notice_period_days: noticeParsed,
        auto_renews: autoRenews,
        renewal_period: autoRenews ? renewalPeriod : 'none',
        cost_minor: costMinor,
        currency,
        billing_cycle: cycle,
        reminder_offsets: parsedOffsets.length > 0 ? parsedOffsets : null,
        note,
        // Field removed from UI — notes cover anything useful. Preserve existing
        // value on edit so we don't wipe historical data; clear on new records.
        reference_last4: existing?.reference_last4 ?? '',
        // Status is changed via Archive / Reactivate, not the save form.
        status: existing?.status ?? 'active',
      },
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert>{error}</Alert>}
      {validation && <Alert>{validation}</Alert>}

      {existing && existing.status !== 'active' && (
        <Alert tone="info">
          This record is <strong>{existing.status}</strong>. It no longer appears on the
          dashboard or triggers reminders.
        </Alert>
      )}

      {/* ── Core ─────────────────────────────────────────────────────────── */}
      <Card className="space-y-4 overflow-hidden">
        <Field label="What is it?" htmlFor="kind">
          <Select id="kind" value={kind} onChange={(e) => changeKind(e.target.value as Kind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="name">
            <Input
              id="name"
              required
              placeholder={placeholders.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field
            label="Provider or issuer"
            htmlFor="provider"
            hint="Optional — who issued or bills it."
          >
            <Input
              id="provider"
              placeholder={placeholders.provider}
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          <Field
            label={kind === 'document' ? 'Expiry date' : 'Expiry or renewal date'}
            htmlFor="expiry"
          >
            <Input
              id="expiry"
              type="date"
              required
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </Field>

          {showNotice && (
            <Field
              label="Act this many days before"
              htmlFor="notice"
              hint="For contracts that need notice to cancel. 30 is common. Leave 0 if the expiry date is the real deadline."
            >
              <Input
                id="notice"
                type="number"
                min={0}
                max={365}
                value={noticeDays}
                onChange={(e) => setNoticeDays(e.target.value)}
              />
            </Field>
          )}
        </div>

        {/* The whole point of the notice period, made visible before saving. */}
        {actionByPreview && Number(noticeDays) > 0 && (
          <Alert tone="info">
            You need to act by <strong>{formatDate(actionByPreview)}</strong> — reminders are sent
            against that date, not the expiry date.
          </Alert>
        )}
      </Card>

      {/* ── Cost (core for subscriptions / contracts / other) ─────────────── */}
      {showCost && (
        <Card className="space-y-4 overflow-hidden">
          <div className="grid min-w-0 gap-4 sm:grid-cols-3">
            <Field label="Cost" htmlFor="cost" hint="Leave blank if it is free.">
              <Input
                id="cost"
                inputMode="decimal"
                placeholder="10.99"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
            </Field>

            <Field label="Currency" htmlFor="currency">
              <Select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Billed" htmlFor="cycle" hint="Used to work out monthly spend.">
              <Select
                id="cycle"
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value as BillingCycle)}
              >
                {BILLING_CYCLES.map((c) => (
                  <option key={c} value={c}>
                    {BILLING_CYCLE_LABELS[c]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Checkbox
            label="This renews automatically"
            hint="The date rolls forward on renewal, so reminders keep coming each cycle."
            checked={autoRenews}
            onChange={(e) => setAutoRenews(e.target.checked)}
          />

          {autoRenews && (
            <Field label="Renews every" htmlFor="renewal">
              <Select
                id="renewal"
                value={renewalPeriod}
                onChange={(e) => setRenewalPeriod(e.target.value as RenewalPeriod)}
              >
                {RENEWAL_PERIODS.filter((p) => p !== 'none').map((p) => (
                  <option key={p} value={p}>
                    {RENEWAL_PERIOD_LABELS[p]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </Card>
      )}

      {/* ── Advanced (collapsed by default) ──────────────────────────────── */}
      <div className="rounded-xl border border-line bg-surface">
        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
          aria-expanded={advancedOpen}
        >
          <span className="text-sm font-medium text-ink">Advanced</span>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-ink-muted transition-transform',
              advancedOpen && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>

        {advancedOpen && (
          <div className="space-y-4 border-t border-line px-4 pt-4 pb-4">
            <Field
              label="Reminder days"
              htmlFor="offsets"
              hint={`Comma-separated days before the deadline. Blank uses your default (${DEFAULT_OFFSETS.join(', ')}).`}
            >
              <Input
                id="offsets"
                placeholder={DEFAULT_OFFSETS.join(', ')}
                value={offsets}
                onChange={(e) => setOffsets(e.target.value)}
              />
            </Field>

            <Field
              label="Note"
              htmlFor="note"
              hint="Cancellation phone number, account details, document number — anything useful later."
            >
              <Textarea
                id="note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" loading={busy}>
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>

        <div className="ml-auto flex flex-wrap gap-2">
          {isActive && onArchive && (
            <Button type="button" variant="secondary" loading={statusBusy} onClick={onArchive}>
              Archive
            </Button>
          )}
          {!isActive && onReactivate && (
            <Button type="button" variant="secondary" loading={statusBusy} onClick={onReactivate}>
              Reactivate
            </Button>
          )}
          {onDelete && (
            <Button type="button" variant="ghost" className="text-overdue" onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      </div>
    </form>
  )
}

/** Parse "90, 30, 7" into [90, 30, 7]. Returns null if anything is not a number. */
function parseOffsets(raw: string): number[] | null {
  const trimmed = raw.trim()
  if (trimmed === '') return []
  const parts = trimmed.split(',').map((p) => p.trim()).filter((p) => p !== '')
  const parsed: number[] = []
  for (const part of parts) {
    const value = Number(part)
    if (!Number.isInteger(value) || value < 0 || value > 3650) return null
    parsed.push(value)
  }
  return [...new Set(parsed)].sort((a, b) => b - a)
}
